import { memo, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { AreaLoadDimensions } from '@scene'
import { find } from 'lodash-es'
import { Draw, OpenInFull } from '@mui/icons-material'
import { Button, Grid, Stack } from '@mui/material'
import { CoordinatePointInput, TextField, UnitConvertedField } from '@ui/forms'
import { useElementLoadStore, useModelStore } from '@editorStores'
import { useSceneElement, useStoreSubscription } from '@editorHooks'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'
import AreaLoadDrawer from '../../AreaLoadDrawer'
import LoadCaseSelect from '../LoadCaseSelect'
import {
  AreaLoadPoint,
  AreaLoadRectangle,
  moveAreaLoadRectangleOrigin,
  toAreaLoadRectangle,
  toAreaLoadRectanglePoints,
  toAreaOfAttack,
} from '../areaLoadRectangle'
import { getElementAreaOfAttack } from '../utils'
import AreaLoadLengthField from './components/AreaLoadLengthField'

// z follows the plane of the element
const originPointManipulation = { z: { disabled: true } }

interface Props {
  selectedElement?: string
  index: number
  active: boolean
}

const AreaLoadFields = ({ selectedElement, index, active }: Props) => {
  const { watch, setValue } = useFormContext()
  const disabled = !selectedElement
  const fieldName = `area-load[${index}]`
  const areaFieldName = `${fieldName}.area_of_attack`
  const editLoadsPerElement = useElementLoadStore(state => state.editLoadsPerElement)
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)

  const {
    params: { drawAreaLoadElement },
    actions: { setDrawAreaLoadElement },
  } = useStructuralPlanningSelection()

  useStoreSubscription({
    writeCallback: editLoadsPerElement,
    fieldName,
    compareValues: true,
    active,
  })

  const elementShape = useMemo(
    () => find([...verticalSlabs, ...verticalRoofSlabs], ['guid', selectedElement]),
    [verticalSlabs, verticalRoofSlabs, selectedElement],
  )

  // derived on every render: the form updates the points in place while P0 is edited
  const points: AreaLoadPoint[] = watch(`${areaFieldName}.points`) ?? []
  const rectangle = toAreaLoadRectangle(points)

  const setArea = useCallback(
    (areaOfAttack: Polygon) => setValue(areaFieldName, areaOfAttack, { shouldDirty: true }),
    [areaFieldName, setValue],
  )

  const setRectangle = (nextRectangle: AreaLoadRectangle) => setArea(toAreaOfAttack(nextRectangle))

  const setEntireElementArea = () => {
    if (!selectedElement) return

    const areaOfAttack = getElementAreaOfAttack(
      [...verticalSlabs, ...verticalRoofSlabs],
      selectedElement,
    )

    if (areaOfAttack) setArea(areaOfAttack)
  }

  // DRAWING

  const isDrawing = active && !!elementShape && drawAreaLoadElement === selectedElement

  const stopDrawing = useCallback(() => setDrawAreaLoadElement(undefined), [setDrawAreaLoadElement])

  const drawerProperties = useMemo(
    () => ({
      onAddLoad: (load: AreaLoad) => {
        setArea(load.area_of_attack)
        stopDrawing()
      },
      onAbortDrawing: stopDrawing,
      drawElementGuid: selectedElement as string,
      elementShape: elementShape as ShapeObject,
    }),
    [elementShape, selectedElement, setArea, stopDrawing],
  )

  useSceneElement({ enabled: isDrawing, component: AreaLoadDrawer, properties: drawerProperties })

  // DIMENSIONS

  const rectanglePointsKey = JSON.stringify(rectangle ? toAreaLoadRectanglePoints(rectangle) : [])

  const dimensionsProperties = useMemo(
    () => ({ points: JSON.parse(rectanglePointsKey) as AreaLoadPoint[] }),
    [rectanglePointsKey],
  )

  useSceneElement({
    enabled: active && !isDrawing && !!rectangle,
    component: AreaLoadDimensions,
    properties: dimensionsProperties,
  })

  const category: LoadCategory = watch(`${fieldName}.load_case.category`)
  const loadCaseType: LoadCaseTypes = watch(`${fieldName}.load_case.load_case_type`)

  return (
    <Grid container spacing={2}>
      <Grid item md={6}>
        <UnitConvertedField
          label="Kraft"
          type="number"
          name={`${fieldName}.force.z`}
          disabled={disabled}
          unitLabel="kN"
        />
      </Grid>
      <Grid item md={6}>
        <LoadCaseSelect
          onSelect={({ identifier, category, loadCaseType }) => {
            setValue(`${fieldName}.load_case.identifier`, identifier)
            setValue(`${fieldName}.load_case.category`, category)
            setValue(`${fieldName}.load_case.load_case_type`, loadCaseType)
          }}
          value={`${category}+${loadCaseType}`}
          referenceName={`${fieldName}.load_case.reference_load_case_type`}
          disabled={disabled}
        />
      </Grid>
      {rectangle && (
        <>
          <Grid item xs={12}>
            <CoordinatePointInput
              label="P0"
              name={`${areaFieldName}.points.0`}
              pointManipulation={originPointManipulation}
              onChangeX={x =>
                x !== null &&
                setRectangle(moveAreaLoadRectangleOrigin(rectangle, x, rectangle.origin.y))
              }
              onChangeY={y =>
                y !== null &&
                setRectangle(moveAreaLoadRectangleOrigin(rectangle, rectangle.origin.x, y))
              }
              onReplace={(x, y) => setRectangle(moveAreaLoadRectangleOrigin(rectangle, x, y))}
            />
          </Grid>
          <Grid item xs={6}>
            <AreaLoadLengthField
              label="l1 (P0 → P1)"
              value={rectangle.l1}
              onChange={l1 => setRectangle({ ...rectangle, l1 })}
              disabled={disabled || isDrawing}
              data-cy={`input-area-load-l1-${index}`}
            />
          </Grid>
          <Grid item xs={6}>
            <AreaLoadLengthField
              label="l2 (P1 → P2)"
              value={rectangle.l2}
              onChange={l2 => setRectangle({ ...rectangle, l2 })}
              disabled={disabled || isDrawing}
              data-cy={`input-area-load-l2-${index}`}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            fullWidth
            startIcon={<OpenInFull />}
            onClick={setEntireElementArea}
            disabled={disabled || !active || !!drawAreaLoadElement}
            data-cy={`btn-entire-element-area-load-${index}`}
          >
            ganze Fläche
          </Button>
          <Button
            size="small"
            variant="outlined"
            fullWidth
            startIcon={<Draw />}
            onClick={() => setDrawAreaLoadElement(selectedElement)}
            disabled={disabled || !active || !!drawAreaLoadElement}
            data-cy={`btn-redraw-area-load-${index}`}
          >
            Fläche zeichnen
          </Button>
        </Stack>
      </Grid>
      <Grid item md={12}>
        <TextField
          label="Kommentar"
          placeholder="Kommentar"
          rows={2}
          name={`${fieldName}.comment`}
          multiline
          disabled={disabled}
        />
      </Grid>
    </Grid>
  )
}

export default memo(AreaLoadFields)
